import { calculateConversationFocusBoxes } from "@/lib/conversation-focus-layout-geometry";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import type { CustomFocusLayout } from "@/lib/focus/focus-types";

const MAX_CONVERSATION_COMPANION_COLUMNS = 4;
const COLLAPSED_TITLE_HEIGHT = 32;
const STACK_GAP = 2;
const MIN_EXPANDED_HEIGHT = 32;

export type ConversationCompanionLayout = Extract<
  CustomFocusLayout,
  { kind: "conversation_companion_stack" }
>;

export function conversationCompanionLayoutSurface(
  layout: ConversationCompanionLayout,
): FocusWindowBox {
  if (layout.surface) return layout.surface;
  const boxes = layout.boxes;
  if (boxes.length === 0) return { x: 0, y: 0, width: 1, height: 1 };
  const x = Math.min(...boxes.map((box) => box.x));
  const y = Math.min(...boxes.map((box) => box.y));
  const right = Math.max(...boxes.map((box) => box.x + box.width));
  const bottom = Math.max(...boxes.map((box) => box.y + box.height));
  return { x, y, width: right - x, height: bottom - y };
}

export function calculateConversationCompanionFocusLayout(
  surface: FocusWindowBox,
  frameIds: string[],
  conversationFrameId: string,
  expandedStackFrameId?: string,
): ConversationCompanionLayout {
  const columnCount = Math.min(
    Math.max(frameIds.length, 1),
    MAX_CONVERSATION_COMPANION_COLUMNS,
  );
  const columns = calculateConversationFocusBoxes(surface, columnCount);
  const boxes = frameIds.map(
    (_, index) => columns[Math.min(index, columnCount - 1)]!,
  );
  const stackFrameIds = frameIds.slice(MAX_CONVERSATION_COMPANION_COLUMNS - 1);
  if (stackFrameIds.length <= 1) {
    return {
      kind: "conversation_companion_stack",
      boxes,
      surface,
      conversationFrameId,
      collapsedFrameIds: [],
    };
  }

  const expandedId = stackFrameIds.includes(expandedStackFrameId ?? "")
    ? expandedStackFrameId!
    : stackFrameIds[stackFrameIds.length - 1]!;
  const allCollapsedFrameIds = stackFrameIds.filter((id) => id !== expandedId);
  const stackColumn = columns[MAX_CONVERSATION_COMPANION_COLUMNS - 1]!;
  const visibleTitleCapacity = Math.max(
    1,
    Math.floor(
      (stackColumn.height - MIN_EXPANDED_HEIGHT) /
        (COLLAPSED_TITLE_HEIGHT + STACK_GAP),
    ),
  );
  const hasHiddenOverflow = allCollapsedFrameIds.length > visibleTitleCapacity;
  const regularTitleCount = hasHiddenOverflow
    ? Math.max(0, visibleTitleCapacity - 1)
    : allCollapsedFrameIds.length;
  const overflowFrameIds = hasHiddenOverflow
    ? allCollapsedFrameIds.slice(
        0,
        allCollapsedFrameIds.length - regularTitleCount,
      )
    : [];
  const overflowTitleFrameId = overflowFrameIds[overflowFrameIds.length - 1];
  const overflowHiddenFrameIds = overflowFrameIds.filter(
    (id) => id !== overflowTitleFrameId,
  );
  const collapsedFrameIds = hasHiddenOverflow
    ? [
        overflowTitleFrameId!,
        ...(regularTitleCount > 0
          ? allCollapsedFrameIds.slice(-regularTitleCount)
          : []),
      ]
    : allCollapsedFrameIds;
  const titleRowsHeight =
    collapsedFrameIds.length * (COLLAPSED_TITLE_HEIGHT + STACK_GAP);
  const expandedBox = {
    ...stackColumn,
    y: stackColumn.y + titleRowsHeight,
    height: Math.max(MIN_EXPANDED_HEIGHT, stackColumn.height - titleRowsHeight),
  };

  for (const frameId of overflowHiddenFrameIds) {
    boxes[frameIds.indexOf(frameId)] = {
      x: stackColumn.x,
      y: stackColumn.y,
      width: stackColumn.width,
      height: 0,
    };
  }
  for (const [index, frameId] of collapsedFrameIds.entries()) {
    const frameIndex = frameIds.indexOf(frameId);
    boxes[frameIndex] = {
      x: stackColumn.x,
      y: stackColumn.y + index * (COLLAPSED_TITLE_HEIGHT + STACK_GAP),
      width: stackColumn.width,
      height: COLLAPSED_TITLE_HEIGHT,
    };
  }
  boxes[frameIds.indexOf(expandedId)] = expandedBox;

  return {
    kind: "conversation_companion_stack",
    boxes,
    surface,
    conversationFrameId,
    collapsedFrameIds,
    expandedStackFrameId: expandedId,
    overflowHiddenFrameIds,
    overflowTitleFrameId,
    overflowTitleCount: overflowFrameIds.length || undefined,
  };
}

export function scaleConversationCompanionLayoutToSurface(
  layout: ConversationCompanionLayout,
  nextSurface: FocusWindowBox,
  frameIds: string[],
): ConversationCompanionLayout {
  const previousSurface = conversationCompanionLayoutSurface(layout);
  if (
    !layout.userAdjusted ||
    previousSurface.width <= 0 ||
    previousSurface.height <= 0
  ) {
    return layout;
  }
  const scaleX = nextSurface.width / previousSurface.width;
  const scaleY = nextSurface.height / previousSurface.height;
  const nextLayout = calculateConversationCompanionFocusLayout(
    nextSurface,
    frameIds,
    layout.conversationFrameId,
    layout.expandedStackFrameId,
  );
  const boxes = [...nextLayout.boxes];
  const scaledBoxes = layout.boxes.map((box) => ({
    x: nextSurface.x + (box.x - previousSurface.x) * scaleX,
    y: nextSurface.y + (box.y - previousSurface.y) * scaleY,
    width: box.width * scaleX,
    height: box.height * scaleY,
  }));
  for (const [index, scaled] of scaledBoxes.entries()) {
    const frameId = frameIds[index];
    if (!frameId) continue;
    if (
      nextLayout.collapsedFrameIds.includes(frameId) ||
      nextLayout.overflowHiddenFrameIds?.includes(frameId)
    ) {
      continue;
    }
    if (frameId === nextLayout.expandedStackFrameId) {
      const canonical = nextLayout.boxes[index]!;
      const stackBottom = canonical.y + canonical.height;
      const y = Math.min(
        Math.max(scaled.y, canonical.y),
        stackBottom - MIN_EXPANDED_HEIGHT,
      );
      boxes[index] = {
        x: scaled.x,
        y,
        width: scaled.width,
        height: Math.max(
          MIN_EXPANDED_HEIGHT,
          Math.min(scaled.height, stackBottom - y),
        ),
      };
      continue;
    }
    boxes[index] = scaled;
  }
  return { ...nextLayout, boxes, userAdjusted: true };
}

export function swapConversationCompanionExpandedFrame(
  layout: ConversationCompanionLayout,
  frameIds: string[],
  frameId: string,
): ConversationCompanionLayout | null {
  const expandedId = layout.expandedStackFrameId;
  const nextExpandedIndex = frameIds.indexOf(frameId);
  const previousExpandedIndex = expandedId ? frameIds.indexOf(expandedId) : -1;
  if (
    !expandedId ||
    nextExpandedIndex < 0 ||
    previousExpandedIndex < 0 ||
    !layout.collapsedFrameIds.includes(frameId)
  ) {
    return null;
  }

  const boxes = [...layout.boxes];
  const expandedBox = boxes[previousExpandedIndex];
  const collapsedBox = boxes[nextExpandedIndex];
  if (!expandedBox || !collapsedBox) return null;
  boxes[nextExpandedIndex] = expandedBox;
  boxes[previousExpandedIndex] = collapsedBox;
  return {
    ...layout,
    boxes,
    collapsedFrameIds: layout.collapsedFrameIds.map((id) =>
      id === frameId ? expandedId : id,
    ),
    expandedStackFrameId: frameId,
  };
}

export function reconcileConversationCompanionFocusLayout(
  layout: ConversationCompanionLayout,
  previousFrameIds: string[],
  nextFrameIds: string[],
  expandedFrameId: string,
): ConversationCompanionLayout {
  const surface = conversationCompanionLayoutSurface(layout);
  const nextLayout = calculateConversationCompanionFocusLayout(
    surface,
    nextFrameIds,
    layout.conversationFrameId,
    expandedFrameId,
  );
  if (!layout.userAdjusted) return nextLayout;

  const previousCanonical = calculateConversationCompanionFocusLayout(
    surface,
    previousFrameIds,
    layout.conversationFrameId,
    layout.expandedStackFrameId,
  );
  const boxes = [...nextLayout.boxes];
  for (const frameId of previousFrameIds.slice(0, 3)) {
    const previousIndex = previousFrameIds.indexOf(frameId);
    const nextIndex = nextFrameIds.indexOf(frameId);
    const actual = layout.boxes[previousIndex];
    const previousBase = previousCanonical.boxes[previousIndex];
    const nextBase = nextLayout.boxes[nextIndex];
    if (!actual || !previousBase || !nextBase || nextIndex < 0) continue;
    const scaleX = nextBase.width / previousBase.width;
    const scaleY = nextBase.height / previousBase.height;
    boxes[nextIndex] = {
      x: nextBase.x + (actual.x - previousBase.x) * scaleX,
      y: nextBase.y + (actual.y - previousBase.y) * scaleY,
      width: actual.width * scaleX,
      height: actual.height * scaleY,
    };
  }
  return { ...nextLayout, boxes, userAdjusted: true };
}
